import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { loginUser } from '../../redux/authSlice'
import { useDispatch, useSelector } from 'react-redux'

export default function AdminLogin() {
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [localError, setLocalError] = useState('')
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { isAuthenticated, error, isLoading } = useSelector(
		(state) => state.auth
	)

	useEffect(() => {
		if (isAuthenticated) {
			navigate('/')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated])

	useEffect(() => {
		if (error) {
			setLocalError(error)
		}
	}, [error])

	const handleLogin = async (e) => {
		e.preventDefault()
		if (!email || !password) {
			setLocalError('Email and password are required')
			return
		}
		setLocalError('')
		dispatch(loginUser({ email, password }))
	}

	return (
		<div className="flex h-screen bg-gray-50">
			{/* Left side - Image with overlay */}
			<div className="hidden lg:flex lg:w-1/2 relative">
				<div
					className="absolute inset-0 bg-cover bg-center z-0"
					style={{
						backgroundImage:
							"url('https://plus.unsplash.com/premium_photo-1682339458664-ca534cd881d5?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8ZGlhb3BtZHN8ZW58MHx8MHx8fDA%3D')",
						backgroundSize: 'cover',
					}}
				/>

				<div className="absolute inset-0 bg-black/40 z-10" />
				<div className="relative z-20 flex flex-col justify-center items-start p-12 text-white">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<h1 className="text-5xl font-bold mb-4">Airah Diamonds</h1>
						<p className="text-xl mb-8 max-w-md">
							Welcome to the exclusive admin portal where brilliance meets
							management excellence.
						</p>
						<div className="space-y-4">
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<path d="M22 12h-4l-3 9L9 3l-3 9H2" />
									</svg>
								</div>
								<p>Monitor real-time diamond inventory</p>
							</div>
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<path d="M12 2v20M2 12h20" />
									</svg>
								</div>
								<p>Manage client relationships seamlessly</p>
							</div>
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<circle cx="12" cy="12" r="10" />
										<path d="M12 8v4l3 3" />
									</svg>
								</div>
								<p>Track sales performance and analytics</p>
							</div>
						</div>
					</motion.div>
				</div>
			</div>

			{/* Right side - Login form */}
			<div className="w-full lg:w-1/2 flex items-center justify-center p-8">
				<motion.div
					className="w-full max-w-md"
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.5 }}
				>
					<div className="text-center mb-10">
						<h2 className="text-3xl font-bold text-gray-800">Admin Login</h2>
						<p className="text-gray-600 mt-2">
							Enter your credentials to access the dashboard
						</p>
					</div>

					{error && (
						<motion.div
							className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700"
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
						>
							<p>{error}</p>
						</motion.div>
					)}

					<form onSubmit={handleLogin} className="space-y-6">
						<div className="space-y-2">
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-700"
							>
								Email Address
							</label>
							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5 text-gray-400"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2m18 0v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6m2-2l8 7 8-7" />
									</svg>
								</div>
								<input
									id="email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-gray-900"
									placeholder="admin@example.com"
								/>
							</div>
						</div>

						<div className="space-y-2">
							<div className="flex justify-between">
								<label
									htmlFor="password"
									className="block text-sm font-medium text-gray-700"
								>
									Password
								</label>
							</div>
							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5 text-gray-400"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
										<path d="M7 11V7a5 5 0 0110 0v4" />
									</svg>
								</div>
								<input
									id="password"
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-gray-900"
									placeholder="••••••••"
								/>
							</div>
						</div>
						<button
							type="submit"
							disabled={isLoading}
							className={`w-full text-md flex justify-center items-center py-3 border border-transparent rounded-md shadow-sm text-white bg-gray-800 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
								isLoading ? 'opacity-70 cursor-not-allowed' : ''
							}`}
						>
							{isLoading ? (
								<>
									<svg
										className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Signing in...
								</>
							) : (
								'Sign in'
							)}
						</button>
					</form>
				</motion.div>
			</div>
		</div>
	)
}
